// Editor utilities: filters, fonts, canvas export

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (e) => reject(e));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

export const FILTER_PRESETS = [
  { id: 'none', name: 'Normale', css: '' },
  { id: 'vivid', name: 'Vivid', css: 'saturate(1.45) contrast(1.1)' },
  { id: 'golden', name: 'ADE Gold', css: 'sepia(0.4) saturate(1.55) contrast(1.05)' },
  { id: 'warm', name: 'Warm', css: 'sepia(0.28) saturate(1.3) brightness(1.05)' },
  { id: 'cool', name: 'Cool', css: 'saturate(1.15) hue-rotate(-14deg) brightness(1.04)' },
  { id: 'fade', name: 'Fade', css: 'contrast(0.85) brightness(1.12) saturate(0.72)' },
  { id: 'bw', name: 'B/N', css: 'grayscale(1) contrast(1.08)' },
  { id: 'noir', name: 'Noir', css: 'grayscale(1) contrast(1.35) brightness(0.88)' },
];

export const DEFAULT_ADJUST = { brightness: 1, contrast: 1, saturate: 1, warmth: 0 };

export const buildFilter = (presetId, adj) => {
  const preset = FILTER_PRESETS.find((p) => p.id === presetId)?.css || '';
  const a = adj || DEFAULT_ADJUST;
  return `${preset} brightness(${a.brightness}) contrast(${a.contrast}) saturate(${a.saturate}) sepia(${a.warmth})`.trim();
};

export const FONTS = [
  { id: 'sans', name: 'Classic', family: "-apple-system, 'Helvetica Neue', Arial, sans-serif", weight: '700' },
  { id: 'black', name: 'Bold', family: "-apple-system, 'Helvetica Neue', Arial, sans-serif", weight: '900' },
  { id: 'serif', name: 'Serif', family: "Georgia, 'Times New Roman', serif", weight: '700' },
  { id: 'mono', name: 'Mono', family: "Menlo, 'Courier New', monospace", weight: '700' },
  { id: 'script', name: 'Script', family: "'Snell Roundhand', 'Brush Script MT', cursive", weight: '700' },
];

export const TEXT_COLORS = ['#FFFFFF', '#000000', '#FF3B5C', '#FFD60A', '#34C759', '#0A84FF', '#BF5AF2', '#FF9500'];

// ADE stickers reuse the gift PNG artwork (brand-coherent)
export const ADE_STICKERS = [
  'gift_box', 'heart_balloon', 'rose_bouquet', 'diamond', 'gold_star', 'hot_air_balloon',
  'headphones', 'ade_perfume', 'yacht', 'ade_watch', 'private_jet', 'ade_shopping',
].map((id) => ({ id, src: `/images/gifts/${id}.png` }));

export const EMOJIS = [
  '😀','😂','😍','😎','🥳','😭','😱','🤩','😜','🥰','😇','🤙',
  '❤️','🔥','✨','💯','⭐','🌈','⚡','💎','👑','🎉','🎁','🏝️',
  '👍','👏','🙌','💪','🫶','✌️','🍕','🍾','☀️','🌙','🌊','🎵',
];

const drawPill = (ctx, text, k) => {
  const paddingX = 14 * k;
  const paddingY = 8 * k;
  const metrics = ctx.measureText(text);
  const w = metrics.width + paddingX * 2;
  const h = parseFloat(ctx.font) + paddingY * 2;
  const r = h / 2;
  ctx.save();
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.beginPath();
  ctx.moveTo(-w / 2 + r, -h / 2);
  ctx.arcTo(w / 2, -h / 2, w / 2, h / 2, r);
  ctx.arcTo(w / 2, h / 2, -w / 2, h / 2, r);
  ctx.arcTo(-w / 2, h / 2, -w / 2, -h / 2, r);
  ctx.arcTo(-w / 2, -h / 2, w / 2, -h / 2, r);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  ctx.fillText(text, 0, 0);
};

// Render base image + strokes + overlay elements into a JPEG blob
export async function exportEditedImage({ imageSrc, filter, elements, strokes, stageW, stageH }) {
  const img = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const k = canvas.width / stageW;
  const ctx = canvas.getContext('2d');

  ctx.filter = filter && filter !== 'none' ? filter : 'none';
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  ctx.filter = 'none';

  // Freehand strokes
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const s of strokes || []) {
    if (!s.points || s.points.length < 2) continue;
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.width * k;
    ctx.beginPath();
    ctx.moveTo(s.points[0].x * k, s.points[0].y * k);
    for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x * k, s.points[i].y * k);
    ctx.stroke();
  }

  // Overlay elements
  for (const el of elements || []) {
    ctx.save();
    ctx.translate(el.x * k, el.y * k);
    ctx.rotate(el.rotation || 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (el.type === 'sticker' || el.type === 'gif') {
      try {
        const stImg = await createImage(el.src);
        const size = (el.base || 96) * el.scale * k;
        const ratio = stImg.naturalHeight / stImg.naturalWidth || 1;
        ctx.drawImage(stImg, -size / 2, (-size * ratio) / 2, size, size * ratio);
      } catch (e) { /* skip broken sticker */ }
    } else if (el.type === 'emoji') {
      const fs = (el.base || 48) * el.scale * k;
      ctx.font = `${fs}px -apple-system, 'Apple Color Emoji', 'Segoe UI Emoji', sans-serif`;
      ctx.fillText(el.content, 0, 0);
    } else if (el.type === 'text') {
      const font = FONTS.find((f) => f.id === el.font) || FONTS[0];
      const fs = (el.base || 30) * el.scale * k;
      ctx.font = `${font.weight} ${fs}px ${font.family}`;
      ctx.fillStyle = el.color || '#fff';
      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = 6 * k;
      const lines = String(el.content).split('\n');
      const lh = fs * 1.2;
      const startY = -((lines.length - 1) * lh) / 2;
      lines.forEach((line, i) => ctx.fillText(line, 0, startY + i * lh));
    } else {
      // mention / hashtag / location pills
      const fs = (el.base || 22) * el.scale * k;
      ctx.font = `700 ${fs}px -apple-system, 'Helvetica Neue', Arial, sans-serif`;
      ctx.fillStyle = '#fff';
      const label = el.type === 'location' ? `📍 ${el.content}` : el.content;
      drawPill(ctx, label, el.scale * k);
    }
    ctx.restore();
  }

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92));
}
